
/**
 * ProcessingView Component
 * Shows processing status and animated progress bar while card is being analyzed
 */
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Loader, RefreshCw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ProcessingViewProps {
  message: string;
  progress: number;
  imageUrl: string | null;
  error: string | null;
  onRetry: () => void;
}

export function ProcessingView({ message, progress, imageUrl, error, onRetry }: ProcessingViewProps) {
  const [showDebug, setShowDebug] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col items-center w-full max-w-xl mx-auto">
      <Card className={`relative overflow-hidden w-full 
        ${isMobile ? 'aspect-[3/4]' : 'aspect-[4/3]'} 
        rounded-lg shadow-xl bg-black`}
      >
        {/* Background image (blurred) */}
        {imageUrl && (
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: `url(${imageUrl})` }}
          >
            <div className="absolute inset-0 backdrop-blur-md bg-black/40"></div>
          </div>
        )}
        
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
          {/* Card preview (smaller) */}
          {imageUrl && (
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="relative mb-6 w-1/2 max-w-[200px]"
            >
              <img 
                src={imageUrl} 
                alt="Captured card" 
                className="w-full h-auto rounded shadow-lg border-2 border-white/20"
              />
              
              {!error && (
                <motion.div 
                  className="absolute -bottom-3 -right-3 w-8 h-8 bg-pokemon-blue rounded-full flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Loader className="h-5 w-5 text-white animate-spin" />
                </motion.div>
              )}
            </motion.div>
          )}
          
          {/* Status message */}
          <motion.h3 
            className="text-xl font-bold mb-2 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {error ? 'Processing Error' : message}
          </motion.h3>
          
          {/* Error display or progress bar */}
          {error ? (
            <motion.div 
              className="mb-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-red-300 mb-4">{error}</p>
              <Button 
                onClick={onRetry}
                variant="outline" 
                className="bg-white/10 border-white/20 hover:bg-white/20 text-white"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              
              <button 
                className="text-xs text-white/50 mt-4 hover:text-white/80"
                onClick={() => setShowDebug(!showDebug)}
              >
                {showDebug ? 'Hide' : 'Show'} Debug Info
              </button>
              
              {showDebug && (
                <div className="mt-2 p-2 bg-black/50 text-left rounded text-xs font-mono text-white/70 max-h-[150px] overflow-y-auto">
                  <pre>{JSON.stringify({imageUrl: imageUrl ? '(image data)' : null, error}, null, 2)}</pre>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div 
              className="w-full max-w-md mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-pokemon-blue"
                  initial={{ width: '5%' }}
                  animate={{ width: `${Math.max(5, progress)}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <div className="flex justify-between mt-1 text-xs text-white/70">
                <span>Analyzing image</span>
                <span>{Math.round(progress)}%</span>
              </div>
            </motion.div>
          )}
        </div>
      </Card>
    </div>
  );
}
